import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { GroupsService } from '@/api';
import type {
  groups_CreateGroupRequest,
  groups_UpdateGroupRequest
} from '@/api';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { errorToastText } from '@/lib/translateBackendError';

// Query keys for groups
export const groupKeys = {
  all: ['groups'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | boolean>) => [...groupKeys.lists(), { filters }] as const,
  details: () => [...groupKeys.all, 'detail'] as const,
  detail: (id: string) => [...groupKeys.details(), id] as const,
};

// Hook to fetch all groups
export function useGroups(limit = 50, offset = 0, enabled = true) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: groupKeys.list({ limit, offset }),
    queryFn: () => GroupsService.getApiV1Groups(limit, offset),
    select: (data) =>
      [...(data || [])].sort(
        (a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.name ?? '').localeCompare(b.name ?? '')
      ),
    enabled: enabled && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch a single group
export function useGroup(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: groupKeys.detail(id),
    queryFn: () => GroupsService.getApiV1Groups1(id),
    enabled: !!id && isSignedIn && isAuthReady,
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to create a group
export function useCreateGroup() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (group: groups_CreateGroupRequest) => {
      return GroupsService.postApiV1Groups(group);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      if (data?.id) {
        queryClient.setQueryData(groupKeys.detail(data.id), data);
      }
      toast.success(i18n._('common.success.created'));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, i18n._('groups.error.createFailed')));
    },
  });
}

// Hook to update a group
export function useUpdateGroup() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: groups_UpdateGroupRequest }) => {
      return GroupsService.putApiV1Groups(id, data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.setQueryData(groupKeys.detail(variables.id), data);
      toast.success(i18n._('common.success.updated'));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, i18n._('groups.error.updateFailed')));
    },
  });
}

// Hook to delete a group
export function useDeleteGroup() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => GroupsService.deleteApiV1Groups(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.removeQueries({ queryKey: groupKeys.detail(variables.id) });
      toast.success(i18n._('common.success.deleted'));
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, i18n._('groups.error.deleteFailed')));
    },
  });
}